import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus } from 'lucide-react';
import { operationalCodes, costCodeKey, costCodeLabel, costCodeProblem, normalizeCostCode, type CostCode } from '@/lib/cost-codes';

/**
 * The Cost Code cell of the Budget table: type to search the company's cost codes (Catalog) by code or name, pick with the mouse or the
 * keyboard, or create a new code from what you typed (it is a real Catalog cost code, available everywhere at once). The list is drawn
 * in a portal so the table's scrolling area never clips it.
 */
interface CostCodeComboboxProps {
  /** the selected cost code id ('' = none) */
  value: string;
  /** shown while the catalog has not loaded the selected code (or no longer has it) */
  fallbackLabel?: string;
  /** the codes that can be chosen (active) plus, via `all`, the ones that may already be selected */
  catalog: CostCode[];
  onChange: (code: CostCode | null) => void;
  /** creates a cost code from the typed text and returns it; rejects with a readable message */
  onCreate?: (text: string) => Promise<CostCode>;
  disabled?: boolean;
  'aria-label'?: string;
}

export function CostCodeCombobox({ value, fallbackLabel = '', catalog, onChange, onCreate, disabled, 'aria-label': ariaLabel = 'Cost code' }: CostCodeComboboxProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [searching, setSearching] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [box, setBox] = useState<{ left: number; top: number; width: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selected = catalog.find((c) => c.id === value);
  const selectable = useMemo(() => operationalCodes(catalog), [catalog]);
  // the box shows the code's label while idle, what is being typed while searching
  const shown = searching ? text : selected ? costCodeLabel(selected) : fallbackLabel;

  const matches = useMemo(() => {
    const term = searching ? text.trim().toLowerCase() : '';
    return term ? selectable.filter((c) => c.code.toLowerCase().includes(term) || (c.name || '').toLowerCase().includes(term)) : selectable;
  }, [selectable, searching, text]);

  const typed = normalizeCostCode(text);
  const canCreate = !!onCreate && searching && typed !== '' && !catalog.some((c) => costCodeKey(c.code) === costCodeKey(typed));
  const typedProblem = canCreate ? costCodeProblem(typed) : null;

  useLayoutEffect(() => {
    if (!open || !inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setBox({ left: rect.left, top: rect.bottom + 2, width: Math.max(rect.width, 260) });
  }, [open, searching, text]);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      const target = event.target as Node;
      if (inputRef.current?.contains(target) || listRef.current?.contains(target)) return;
      setOpen(false);
      setSearching(false);
    };
    const reposition = () => setOpen(false);
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  useEffect(() => { setHighlighted(0); setError(null); }, [text, open]);

  const choose = (code: CostCode | null) => {
    onChange(code);
    setOpen(false);
    setSearching(false);
    inputRef.current?.blur();
  };

  const create = async () => {
    if (!onCreate || creating || typedProblem) return;
    setCreating(true);
    setError(null);
    try {
      choose(await onCreate(typed));
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Could not create the cost code');
    } finally {
      setCreating(false);
    }
  };

  // the entries in display order: matches..., [create]
  const entries: Array<{ kind: 'code'; entry: CostCode } | { kind: 'create' }> = [
    ...matches.map((entry) => ({ kind: 'code' as const, entry })),
    ...(canCreate ? [{ kind: 'create' as const }] : []),
  ];

  const pick = (index: number) => {
    const item = entries[index];
    if (!item) return;
    if (item.kind === 'code') choose(item.entry);
    else void create();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') { event.preventDefault(); setOpen(true); setHighlighted((i) => Math.min(i + 1, entries.length - 1)); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setHighlighted((i) => Math.max(i - 1, 0)); }
    else if (event.key === 'Enter' && open) { event.preventDefault(); if (entries.length > 0) pick(Math.min(highlighted, entries.length - 1)); }
    else if (event.key === 'Escape') { setOpen(false); setSearching(false); inputRef.current?.blur(); }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-autocomplete="list"
        disabled={disabled}
        value={shown}
        placeholder="Cost code"
        title={shown}
        onFocus={() => { setText(''); setOpen(true); }}
        onChange={(event) => { setText(event.target.value); setSearching(true); setOpen(true); }}
        onKeyDown={onKeyDown}
        onBlur={() => { setOpen(false); setSearching(false); }}
        className="w-full h-5 bg-transparent border-0 shadow-none px-2 text-sm leading-5 truncate focus:outline-none focus:bg-white focus:ring-1 focus:ring-blue-500 rounded"
      />
      {open && box && createPortal(
        <div
          ref={listRef}
          role="listbox"
          style={{ position: 'fixed', left: box.left, top: box.top, width: box.width, zIndex: 1000 }}
          className="bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-auto py-1"
        >
          {entries.map((item, index) => {
            const active = index === highlighted;
            const base = `px-3 py-1.5 text-sm cursor-pointer flex items-center gap-2 ${active ? 'bg-gray-100' : 'hover:bg-gray-100'}`;
            if (item.kind === 'create') {
              return (
                <div key="create" role="option" aria-selected={false} aria-disabled={!!typedProblem} className={`${base} ${typedProblem ? 'text-gray-400 cursor-not-allowed' : 'text-blue-700'}`} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(index)}>
                  <Plus className="w-3.5 h-3.5" /> Create “{typed}”
                </div>
              );
            }
            return (
              <div key={item.entry.id} role="option" aria-selected={item.entry.id === value} className={base} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(index)}>
                <span className="font-medium">{item.entry.code}</span>
                {item.entry.name && <span className="text-gray-500 truncate">{item.entry.name}</span>}
              </div>
            );
          })}
          {entries.length === 0 && <div className="px-3 py-2 text-sm text-gray-500">No matching cost code</div>}
          {(typedProblem || error) && <div className="px-3 py-1.5 text-xs text-red-700" role="alert">{error || typedProblem}</div>}
        </div>,
        document.body
      )}
    </>
  );
}
